/**
 * Задачи скаута: схемы и хуки.
 *
 * Схемы перенесены из sharing-admin/src/api/ops.ts, но НЕ скопированы:
 * админская схема теряет четыре поля, которые бэкенд реально отдаёт
 * (`qr_token`, `imei`, `lat`, `lng` — public.proto, ScoutTask 12..15).
 * Диспетчеру за монитором они не нужны, скауту в поле — это ровно то, чем
 * он находит байк. Поля добавлены здесь.
 *
 * У каждого поля обязателен .default(...): grpc-gateway опускает нулевые
 * значения, и без дефолта схема развалится на первом же пустом ответе.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { request } from '@/lib/http'
import { useAuth } from '@/lib/auth'
import { nullAsMissing } from './nested'
import type { LatLng } from '@/lib/geo'
import {
  enqueue,
  queuedTargets,
  subscribeOutbox,
  type OutboxEntry,
} from '@/lib/offline/outbox'

export const SCOUT_TASK_TYPES = ['pickup', 'return', 'writeoff_pickup', 'rebalance'] as const
export type ScoutTaskType = (typeof SCOUT_TASK_TYPES)[number]

export const scoutTaskTypeLabel: Record<string, string> = {
  pickup: 'Забрать транспорт',
  return: 'Вернуть в строй',
  writeoff_pickup: 'Забрать на списание',
  rebalance: 'Переставить',
}

export const scoutTaskStatusLabel: Record<string, string> = {
  open: 'Свободна',
  claimed: 'Взята',
  done: 'Выполнена',
  cancelled: 'Отменена',
}

export const scoutTaskSchema = z.object({
  id: z.string().default(''),
  type: z.string().default(''),
  vehicleId: z.string().default(''),
  workOrderId: z.string().default(''),
  centerId: z.string().default(''),
  assigneeUserId: z.string().default(''),
  status: z.string().default(''),
  note: z.string().default(''),
  // C1/C2: причина текстом — авто-задача от геозоны или диспетчерская
  // пометка. Пусто — читаем note (старый бэкенд писал причину прямо в неё).
  reason: z.string().default(''),
  // Геокодер есть не всегда — пусто, если адрес не резолвился.
  address: z.string().default(''),
  createdAt: z.string().default(''),
  doneAt: z.string().default(''),
  targetZoneId: z.string().default(''),
  // Приметы байка для поля. Заполняются на списочных ручках, нули — если неизвестно.
  qrToken: z.string().default(''),
  // C1: человеческий номер. До выката BE-2/BE-3 сервер его не отдаёт —
  // экраны читают его через displayNumber() (bike-view.ts), которая
  // откатывается на qrToken.
  number: z.string().default(''),
  imei: z.string().default(''),
  lat: z.number().default(0),
  lng: z.number().default(0),
  // Живой снимок из Redis: заряд и состояние замка.
  //
  // ⚠ У runtimeStale ТРИ состояния, и `.default()` здесь ставить нельзя.
  // На проводе это `optional bool`, поэтому:
  //   undefined — сервер про телеметрию вообще не говорит (старый бэкенд);
  //   true      — снимок есть, но протух: заряд неизвестен;
  //   false     — снимок свежий, batteryPct и locked настоящие.
  // Дефолт схлопнул бы первые два в одно, а обычный proto3-bool схлопнул бы
  // первое с третьим: false в JSON опускается, и заряженный байк читался бы
  // как потерявший связь.
  batteryPct: z.number().default(0),
  locked: z.boolean().default(false),
  runtimeStale: z.boolean().optional(),
  vehicleStatus: z.string().default(''),
  lastSeenAt: z.string().default(''),
  // Кто завёл задачу. У своей задачи скаута совпадает с assigneeUserId —
  // «Забрать» теперь сразу назначает её на него; у задачи диспетчера не
  // совпадает. Различить их иначе нечем: обе приходят в статусе claimed.
  createdBy: z.string().default(''),
  // Пока нет в public.proto ScoutTask (см. api/public/v1/public.proto) — optional,
  // не .default(): отсутствие поля значит «сервер не знает», а не «выключен».
  headlightOn: z.boolean().optional(),
})

/** Что показывать про заряд и связь. Три состояния, а не два. */
export type TelemetryState = 'unknown' | 'stale' | 'fresh'

export function telemetryState(task: ScoutTask): TelemetryState {
  if (task.runtimeStale === undefined) return 'unknown'
  return task.runtimeStale ? 'stale' : 'fresh'
}
export type ScoutTask = z.infer<typeof scoutTaskSchema>

/** Номер байка по задаче: C1-поле или, до выката, qrToken задачи. */
export function taskDisplayNumber(t: Pick<ScoutTask, 'number' | 'qrToken'>): string {
  return t.number || t.qrToken
}

/** Причина задачи текстом: C1-поле `reason` или, до его выката, `note`. */
export function taskReasonText(t: Pick<ScoutTask, 'reason' | 'note'>): string {
  return t.reason || t.note
}

const scoutTasksReply = z.object({ tasks: z.array(scoutTaskSchema).default([]) })

export const QK = {
  pool: ['scout.tasks.pool'] as const,
  mine: ['scout.tasks.mine'] as const,
}

/**
 * Пул свободных задач. Бэкенд отдаёт только status=open.
 *
 * ⚠ ГЕЙТ РОЛИ ЖИВЁТ ЗДЕСЬ, А НЕ НА ЭКРАНАХ. Пул читают трое: экран задач,
 * карточка байка и `useVehiclesWithTask` (карта и список парка). Спрятать
 * раздел на одном экране значило бы оставить два молчаливых 403 в фоне у
 * каждого партнёра — ручка ему закрыта (STAFF_REQUIRED).
 *
 * ⚠ У выключенного запроса `isLoading === false`. Экраны, которые складывают
 * его в общий признак загрузки, обязаны это учитывать — иначе партнёр на
 * холодном старте увидит «не найдено» вместо «загружаем».
 */
export function useTaskPool(limit = 200) {
  const { isPartner } = useAuth()
  return useQuery({
    queryKey: [...QK.pool, limit],
    queryFn: () => request('/v1/scout/tasks/pool', scoutTasksReply, { query: { limit } }),
    enabled: !isPartner,
    // Пул живёт недолго: задачу могли забрать. Но и не дёргаем на каждый фокус —
    // скаут на мобильном интернете.
    staleTime: 30_000,
  })
}

/**
 * Задачи текущего скаута — только взятые.
 *
 * `status` передаётся обязательно. Без него бэкенд отдаёт все задачи, когда-либо
 * назначенные на пользователя, любого статуса, срезом в 100 штук по убыванию
 * даты создания — и после сотни закрытых задач активная выпадает из среза
 * и не приходит вовсе. Диспетчерская доска в админке читает ту же ручку без
 * фильтра, ей нужна и история, поэтому фильтр — параметр, а не новый дефолт.
 */
export function useMyTasks(limit = 200) {
  return useQuery({
    queryKey: [...QK.mine, limit],
    queryFn: () =>
      request('/v1/scout/tasks/mine', scoutTasksReply, {
        query: { limit, status: 'claimed' },
      }),
    staleTime: 30_000,
  })
}

/**
 * Активные задачи — то, что скаут должен сделать сейчас.
 *
 * Фильтр остаётся на клиенте, хотя `status=claimed` уже уходит на сервер, и
 * это не дублирование, а защита от старого сервера. Kratos **молча
 * игнорирует** незнакомые query-параметры (проверено: binding.BindQuery
 * принимает лишний ключ без ошибки), поэтому бэкенд без поддержки `status`
 * вернёт все статусы и не подаст ни малейшего признака. Без этой строки на
 * таком сервере в «В работе» окажутся выполненные и отменённые задачи.
 *
 * Убирать только тогда, когда минимальная поддерживаемая версия бэкенда
 * гарантированно умеет фильтровать, — а не тогда, когда её научили.
 */
export function selectActive(tasks: ScoutTask[]): ScoutTask[] {
  return tasks.filter((t) => t.status === 'claimed')
}

/**
 * Взять задачу.
 *
 * Всегда через очередь, даже когда сеть есть: так «взял» и «завершил»
 * гарантированно уходят в одном порядке и с одним ключом идемпотентности,
 * и не существует второго пути, который об этом не знает.
 */
export function useClaimTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (taskId: string) => enqueue('claim', taskId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.pool })
      void qc.invalidateQueries({ queryKey: QK.mine })
    },
  })
}

/**
 * Завести задачу «Забрать» на байк.
 *
 * Через очередь, как всё остальное: скаут стоит у байка за зоной, и сеть там
 * бывает не чаще, чем в паркинге. Второго пути к API для мутаций нет —
 * правило CLAUDE.md.
 *
 * ⚠ Дубли отбивает СЕРВЕР (VEHICLE_HAS_ACTIVE_TASK,
 * fleet/internal/biz/maintenance.go). Клиентские проверки ниже лишь экономят
 * поездку в очередь; арбитр не они.
 *
 * ⚠ ЗАДАЧА ПРИХОДИТ УЖЕ ВЗЯТОЙ. Сервер назначает её на создателя тем же
 * запросом (`CreateScoutTask` в public-api): раньше она падала в общий пул, и
 * скаут вторым нажатием отбирал её у самого себя — иногда неудачно, потому
 * что «Взять» срабатывает только пока задача свободна. Отсюда инвалидация
 * ОБОИХ списков: новая задача появится в «В работе», а не в пуле.
 */
export function useCreatePickupTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, note }: { vehicleId: string; note: string }) =>
      enqueue('create_pickup', vehicleId, { note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.pool })
      void qc.invalidateQueries({ queryKey: QK.mine })
    },
  })
}

/**
 * Завести задачу «Переставить» на байк.
 *
 * Отличается от «Забрать» не только типом: сервер создаёт её уже взятой И
 * сразу переводит байк в `in_transit` (`loaded: true`, см. bodyFor). То есть
 * нажатие — это не заявка, а заявление «байк уже у меня»: с этой секунды он
 * не бронируется клиентами, а его перемещение не считается кражей.
 *
 * Отсюда же лимит на стороне сервера: одно активное задание на исполнителя
 * (409 `PARTNER_TASK_ACTIVE`). Столько байков в руках всё равно не унести, а
 * набрать их «про запас» значило бы вывести из парка то, что стоит на месте.
 */
export function useCreateRebalanceTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, note }: { vehicleId: string; note: string }) =>
      enqueue('create_rebalance', vehicleId, { note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.pool })
      void qc.invalidateQueries({ queryKey: QK.mine })
    },
  })
}

/**
 * Байки, на которые задача, НАСКОЛЬКО МЫ ЗНАЕМ, уже есть.
 *
 * ⚠ ЭТО НИЖНЯЯ ОЦЕНКА, А НЕ ИСТИНА, и полагаться на неё нельзя. Пул отдаёт
 * только открытые задачи, «мои» — только взятые ЭТИМ скаутом. Задача, взятая
 * другим скаутом, не видна ни там, ни там, и кнопка «Забрать» на таком байке
 * останется активной. Настоящий арбитр — VEHICLE_HAS_ACTIVE_TASK на сервере;
 * поэтому его отказ показывается спокойным тоном, а не как сбой.
 *
 * Третье слагаемое — очередь: без сети сервер ответить не может, и только она
 * не даёт нажать «Забрать» дважды.
 */
export function useVehiclesWithTask(): Set<string> {
  const pool = useTaskPool()
  const mine = useMyTasks()
  const [pending, setPending] = useState<OutboxEntry[]>([])

  useEffect(() => subscribeOutbox(setPending), [])

  return useMemo(() => {
    const ids = new Set<string>()
    for (const t of pool.data?.tasks ?? []) if (t.vehicleId) ids.add(t.vehicleId)
    for (const t of selectActive(mine.data?.tasks ?? [])) if (t.vehicleId) ids.add(t.vehicleId)
    for (const id of queuedTargets(pending, 'create_pickup')) ids.add(id)
    // Оба вида заводят задачу на байк, и оба обязаны гасить кнопку: без сети
    // очередь — единственная защита от второго нажатия.
    for (const id of queuedTargets(pending, 'create_rebalance')) ids.add(id)
    return ids
  }, [pool.data, mine.data, pending])
}

export type TaskCompletion = {
  /**
   * Куда переводить байк. Опущено — сервер ставит `servicing`, и это верно
   * для «Забрать»: забранный байк едет в ремонт.
   *
   * ⚠ Поле называется `result`, а НЕ `outcome`. Здесь стоял `outcome`, поля
   * с таким именем в контракте нет, и сервер молча его игнорировал.
   */
  result?: 'available' | 'at_base' | 'servicing'
  /** Фактическая точка завершения — где скаут стоял, а не где числился байк. */
  lat?: number
  lng?: number
}

/**
 * Чем завершается задача. `null` — завершать нечем, кнопку надо погасить.
 *
 * Перестановка — единственный тип, у которого есть обязательное условие.
 * Обычно бэкенд требует у `rebalance → available` целевую зону
 * (`target_zone_id`, fleet/internal/biz/maintenance.go); у полевого
 * исполнителя зоны нет — он ставит байк там, где для него есть место.
 * Поэтому сервер принимает вместо зоны КООРДИНАТЫ, и без них перестановку
 * завершить нельзя: байк остался бы `in_transit`, то есть выведенным из парка.
 *
 * Отсюда и `null`, а не «отправим без координат»: отказ на кнопке честнее
 * молчаливой потери задачи через полчаса, когда исполнитель уже уехал.
 */
export function completionFor(task: ScoutTask, me: LatLng | null): TaskCompletion | null {
  const where = me ? { lat: me.lat, lng: me.lng } : {}
  if (task.type === 'rebalance') {
    if (!me) return null
    return { result: 'available', ...where }
  }
  // Остальные типы результат не назначают — серверный дефолт `servicing`
  // и есть то, что нужно «Забрать».
  return where
}

/**
 * Завершить задачу.
 *
 * Тело собирается `completionFor` — единственным местом, которое знает,
 * что значит «завершено» для каждого типа задачи.
 */
export function useCompleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, completion }: { taskId: string; completion: TaskCompletion }) =>
      enqueue('complete', taskId, { ...completion }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.mine })
      void qc.invalidateQueries({ queryKey: QK.pool })
    },
  })
}

/* ─── Профиль ─────────────────────────────────────────────────────────── */

/** public.proto message User (700..709). */
const userSchema = z.object({
  id: z.string().default(''),
  phone: z.string().default(''),
  roles: z.array(z.string()).default([]),
  createdAt: z.string().default(''),
  displayName: z.string().default(''),
  avatarUrl: z.string().default(''),
  locale: z.string().default(''),
  banned: z.boolean().default(false),
})
export type User = z.infer<typeof userSchema>

/**
 * GET /v1/me отдаёт GetUserReply — пользователь ВЛОЖЕН в поле `user`,
 * рядом лежит подписка (скауту не нужна, но поле существует).
 * Плоская схема здесь была бы молчаливо пустой на каждом поле.
 */
const meSchema = z.object({
  user: nullAsMissing(userSchema.default({
    id: '',
    phone: '',
    roles: [],
    createdAt: '',
    displayName: '',
    avatarUrl: '',
    locale: '',
    banned: false,
  })),
})

export function useMe() {
  return useQuery({
    queryKey: ['scout.me'],
    queryFn: async () => (await request('/v1/me', meSchema)).user,
    staleTime: 5 * 60_000,
  })
}
